import React from 'react'
import { useIntl } from 'react-intl'
import { DiagnosticCollectionInfo } from './form-components/diagnostic-collection-info'
import { DiagnosticCollectionDisclaimer } from './form-components/diagnostic-collection-disclaimer'
import { Row, FormValidator, Box } from '../../legacy-design-system/form'
import { Form } from './form-components/form'
import { CancelButton } from './form-components/cancel-button'
import { CloseButton } from './form-components/close-button'
import { DoneButton } from './form-components/done-button'
import {
  DiagnosticCollectionFormValue,
  DiagnosticCollectionSetterFactory
} from './index'
import { DiagnosticCollectionSettings, LogLevel } from '../servers-grid/shared'
import { DiagnosticCollectionButtonEnable } from './form-components/diagnostic-collection-button-enable'
import { DiagnosticCollectionDisableForm } from './form-components/diagnostic-collection-disable-form'
import { DiagnosticCollectionEnableForm } from './form-components/diagnostic-collection-enable-form'
import {
  Modal,
  ModalFooter
} from '@contrast-security-inc/design-system-popovers'

const dialogSize = 'lg'

export function validDurationDisplay(logLevel: LogLevel, duration: number) {
  // do not accept fractions
  if (duration % 1 !== 0) {
    return false
  }

  if (logLevel === 'DEBUG') {
    return duration >= 1 && duration <= 24
  } else if (logLevel === 'TRACE') {
    return duration >= 1 && duration <= 6
  }

  return false
}

export const isSaveButtonDisabled = ({
  hasUnsavedChanges,
  formIsValid,
  formValue
}: {
  hasUnsavedChanges: boolean
  formIsValid: boolean
  formValue: DiagnosticCollectionFormValue
}) => {
  return (
    !hasUnsavedChanges ||
    (hasUnsavedChanges && !formIsValid) ||
    !validDurationDisplay(formValue.logLevel, formValue.duration)
  )
}

export interface DiagnosticCollectionFormProps {
  isOpen: boolean
  formValue: DiagnosticCollectionFormValue
  diagnosticCollectionSettings: DiagnosticCollectionSettings
  onCancel: () => void
  onConfirm: (value: DiagnosticCollectionFormValue) => void
  formSetter: DiagnosticCollectionSetterFactory

  hasUnsavedChanges: boolean
  isReadOnly?: boolean
}

export function DiagnosticCollectionFormView({
  diagnosticCollectionSettings,
  isOpen,
  formValue,
  onCancel,
  onConfirm,
  formSetter,
  hasUnsavedChanges,
  isReadOnly = false,
  ...stateMgrProps
}: DiagnosticCollectionFormProps) {
  const { formatMessage } = useIntl()
  return (
    <FormValidator value={formValue}>
      {({ formIsValid }) => {
        return (
          <Modal
            size={dialogSize}
            variant="info"
            title={formatMessage({
              id: 'title_servers_setDiagnosticCollection'
            })}
            isOpen={isOpen}
            onClose={onCancel}
          >
            <Form>
              <Row>
                <Box>
                  <DiagnosticCollectionInfo />
                </Box>
              </Row>
              <Row>
                {diagnosticCollectionSettings.enabled ? (
                  <DiagnosticCollectionDisableForm
                    {...stateMgrProps}
                    formValue={formValue}
                    diagnosticCollectionSettings={diagnosticCollectionSettings}
                    onClick={onConfirm}
                  />
                ) : (
                  <DiagnosticCollectionEnableForm
                    {...stateMgrProps}
                    formValue={formValue}
                    formSetter={formSetter}
                    isReadOnly={isReadOnly}
                  />
                )}
              </Row>
              <Row>
                <Box>
                  <DiagnosticCollectionDisclaimer />
                </Box>
              </Row>
            </Form>
            <ModalFooter>
              {isReadOnly ? (
                <DoneButton onClick={onCancel} />
              ) : (
                <>
                  {diagnosticCollectionSettings.enabled ? (
                    <CloseButton onClick={onCancel} />
                  ) : (
                    <>
                      <CancelButton onClick={onCancel} />
                      <DiagnosticCollectionButtonEnable
                        disabled={isSaveButtonDisabled({
                          hasUnsavedChanges,
                          formIsValid,
                          formValue
                        })}
                        onClick={onConfirm}
                      />
                    </>
                  )}
                </>
              )}
            </ModalFooter>
          </Modal>
        )
      }}
    </FormValidator>
  )
}
